import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { coursesApi, orgApi, supportApi } from '../../api/client';

const SupportPage: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>({ message: '', supportAction: '' });

  const load = async () => {
    try {
      const [s, e, c] = await Promise.all([
        supportApi.list(),
        orgApi.listEmployees(),
        coursesApi.list(),
      ]);
      setItems(s);
      setEmployees(e);
      setCourses(c);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    try {
      const emp = employees.find((e) => e.id === form.employeeId);
      const course = courses.find((c) => c.id === form.courseId);
      await supportApi.create({
        employeeId: form.employeeId,
        courseId: form.courseId,
        supportAction: form.supportAction || 'message',
        notes: form.message,
        sendEmail: true,
        employeeEmail: emp?.email,
        employeeName: emp?.name,
        courseTitle: course?.title,
        message: form.message,
      });
      setOpen(false);
      setForm({ message: '', supportAction: '' });
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4">Employees Needing Support</Typography>
        <Button variant="contained" onClick={() => setOpen(true)}>Send support email</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {items.map((s) => (
        <Box key={s.id} sx={{ p: 2, mb: 1, border: '1px solid #ddd', borderRadius: 1 }}>
          <Typography fontWeight={600}>
            {s.employee?.name} · {s.course?.title}
          </Typography>
          <Typography variant="body2">{s.notes}</Typography>
          <Typography variant="caption">{new Date(s.providedAt).toLocaleString()}</Typography>
        </Box>
      ))}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark Support as Provided</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Employee</InputLabel>
              <Select value={form.employeeId || ''} label="Employee" onChange={(e) => setForm({ ...form, employeeId: e.target.value })}>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>{emp.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Course</InputLabel>
              <Select value={form.courseId || ''} label="Course" onChange={(e) => setForm({ ...form, courseId: e.target.value })}>
                {courses.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Divider sx={{ my: 2 }} />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Support Action</InputLabel>
              <Select value={form.supportAction || ''} label="Support Action" onChange={(e) => setForm({ ...form, supportAction: e.target.value })}>
                <MenuItem value="1on1">1-on-1 Training Session</MenuItem>
                <MenuItem value="message">Encouragement Message Sent</MenuItem>
                <MenuItem value="resources">Supplemental Resources Assigned</MenuItem>
                <MenuItem value="coaching">Coaching Session Scheduled</MenuItem>
                <MenuItem value="reset">Quiz Attempts Reset</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Notes"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Add any notes about the support provided..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={!form.supportAction || !form.employeeId}>
            Mark as Provided
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportPage;
